/**
 * Gantt Page - Dedicated full-screen Gantt Chart with Drag & Drop Rescheduling
 * Provides scenario selector and full GanttChartWidget with reschedule panel.
 */

import { useState, useMemo } from 'react';
import {
  Calendar,
  ChevronDown,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Clock,
  Layers,
} from 'lucide-react';
import { Layout } from '../components/layout';
import { GanttChartWidget } from '../components/widgets/GanttChartWidget';
import { useGanttData, type GanttOperation } from '../hooks/useGanttData';
import { usePlanningScenarios, type PlanningScenario } from '../hooks/usePlanningScenarios';

type GroupBy = 'work_center' | 'order';

export function GanttPage() {
  const [selectedScenarioId, setSelectedScenarioId] = useState<string>('');
  const [groupBy, setGroupBy] = useState<GroupBy>('work_center');
  const [showScenarioDropdown, setShowScenarioDropdown] = useState(false);

  // Fetch all scenarios
  const scenarios = usePlanningScenarios();
  const scenarioList = scenarios.data ?? [];

  // Auto-select first active/completed scenario
  const activeScenarios = useMemo(() => {
    return scenarioList.filter(
      (s) => s.status === 'approved' || s.status === 'applied' || s.status === 'pending_review'
    );
  }, [scenarioList]);

  // If no scenario selected but we have scenarios, auto-select
  const effectiveScenarioId = selectedScenarioId || activeScenarios[0]?.id || '';

  // Fetch Gantt data for selected scenario
  const ganttData = useGanttData(effectiveScenarioId, groupBy, !!effectiveScenarioId);

  const selectedScenario = scenarioList.find((s) => s.id === effectiveScenarioId);

  const handleOperationClick = (op: GanttOperation) => {
    // Could open detail panel in future
    console.log('Operation clicked:', op.order_number, op.operation_sequence);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'applied':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'approved':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'pending_review':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'calculating':
        return 'bg-cyan-100 text-cyan-700 border-cyan-200';
      case 'draft':
        return 'bg-gray-100 text-gray-600 border-gray-200';
      case 'rejected':
      case 'failed':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  return (
    <Layout title="Gantt Chart">
      <div className="flex flex-col h-full">
        {/* Header Bar */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-4">
            <Calendar className="w-5 h-5 text-[#45bdd6]" />
            <h1 className="text-lg font-semibold text-gray-900">
              Production Planning — Gantt
            </h1>
          </div>

          <div className="flex items-center gap-3">
            {/* Scenario Selector */}
            <div className="relative">
              <button
                onClick={() => setShowScenarioDropdown(!showScenarioDropdown)}
                className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:bg-gray-50 min-w-[260px]"
              >
                <Layers className="w-4 h-4 text-gray-400" />
                <span className="flex-1 text-left truncate">
                  {selectedScenario
                    ? selectedScenario.name
                    : scenarios.isLoading
                    ? 'Loading scenarios...'
                    : 'Select scenario...'}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {showScenarioDropdown && (
                <div className="absolute right-0 top-full mt-1 w-[360px] bg-white border border-gray-200 rounded-lg shadow-xl z-30 max-h-[400px] overflow-auto">
                  {scenarios.isLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                    </div>
                  ) : scenarioList.length === 0 ? (
                    <div className="py-6 text-center text-sm text-gray-400">
                      <Calendar className="w-8 h-8 mx-auto mb-2" />
                      No scenarios available
                    </div>
                  ) : (
                    <div className="py-1">
                      {scenarioList.map((scenario) => (
                        <button
                          key={scenario.id}
                          onClick={() => {
                            setSelectedScenarioId(scenario.id);
                            setShowScenarioDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                            scenario.id === effectiveScenarioId
                              ? 'bg-cyan-50 border-l-2 border-[#45bdd6]'
                              : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm text-gray-900 truncate">
                              {scenario.name}
                            </span>
                            <span
                              className={`text-[10px] uppercase font-medium px-2 py-0.5 rounded-full border ${getStatusColor(
                                scenario.status
                              )}`}
                            >
                              {scenario.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(scenario.planning_start).toLocaleDateString('en-GB')} —{' '}
                              {new Date(scenario.planning_end).toLocaleDateString('en-GB')}
                            </span>
                            {scenario.on_time_rate != null && (
                              <span className="flex items-center gap-1">
                                <CheckCircle className="w-3 h-3 text-green-500" />
                                {(scenario.on_time_rate * 100).toFixed(0)}% OTD
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Group By Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setGroupBy('work_center')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  groupBy === 'work_center'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Work Center
              </button>
              <button
                onClick={() => setGroupBy('order')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  groupBy === 'order'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Order
              </button>
            </div>

            {/* Refresh */}
            <button
              onClick={() => ganttData.refetch()}
              disabled={ganttData.isRefetching}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <RefreshCw
                className={`w-4 h-4 ${ganttData.isRefetching ? 'animate-spin' : ''}`}
              />
            </button>
          </div>
        </div>

        {/* Close dropdown on outside click */}
        {showScenarioDropdown && (
          <div
            className="fixed inset-0 z-20"
            onClick={() => setShowScenarioDropdown(false)}
          />
        )}

        {/* Gantt Chart - Full Height */}
        <div className="flex-1 p-4 bg-gray-50 overflow-hidden">
          {!effectiveScenarioId ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400">
              <Calendar className="w-12 h-12" />
              <p className="text-lg font-medium">No Scenario Selected</p>
              <p className="text-sm">
                Select a planning scenario above to see the Gantt chart.
              </p>
              {activeScenarios.length === 0 && scenarioList.length === 0 && !scenarios.isLoading && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start gap-2 max-w-md">
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-700">
                    There are no planning scenarios yet. First create a scenario
                    under &quot;AI Planner&quot; or &quot;Scheduling&quot;.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 h-full overflow-hidden">
              <GanttChartWidget
                data={ganttData.data}
                loading={ganttData.isLoading}
                error={ganttData.error ?? null}
                onRefresh={() => ganttData.refetch()}
                onOperationClick={handleOperationClick}
                scenarioId={effectiveScenarioId}
                height="100%"
              />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
